import React, { useState } from "react";
import { ArrowRight, Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminAuthFrame } from "@/components/AdminAuthFrame";
import { supabase } from "@/lib/supabase";

const AdminRegister: React.FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Please complete your name and email address.");
    if (password.length < 8) return toast.error("Your password must contain at least 8 characters.");
    if (password !== confirmPassword) return toast.error("The passwords do not match.");

    setSubmitting(true);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: name.trim() },
        emailRedirectTo: `${window.location.origin}/admin`,
      },
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);

    if (data.session) {
      const { data: isAdmin } = await supabase.rpc("is_inventory_admin");
      if (!isAdmin) await supabase.auth.signOut();
    }
    setEmailSent(true);
    toast.success("Verification email sent.");
  };

  if (emailSent) {
    return (
      <AdminAuthFrame eyebrow="Verify Your Email" title="Check your inbox" description="We sent a secure verification link to your email address.">
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><Mail className="h-7 w-7" /></span>
          <h2 className="mt-4 text-lg font-bold text-slate-900">Verification required</h2>
          <p className="mt-2 text-sm text-slate-500">Open the email sent to <strong className="text-slate-700">{email}</strong> and select “Verify admin email”. Only approved admin addresses receive dashboard access.</p>
          <Link to="/admin/login" className="btn-blue mt-6 w-full">Return to Login <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </AdminAuthFrame>
    );
  }

  return (
    <AdminAuthFrame eyebrow="Authorised Registration" title="Create Admin Account" description="Register with an approved Simphiwe Motors admin email address.">
      <form onSubmit={submit} className="space-y-4">
        <AuthInput icon={User} label="Full Name" value={name} onChange={setName} placeholder="Your full name" testId="register-name-input" autoComplete="name" />
        <AuthInput icon={Mail} label="Email Address" type="email" value={email} onChange={setEmail} placeholder="admin@example.co.za" testId="register-email-input" autoComplete="email" />
        <PasswordInput label="Password" value={password} onChange={setPassword} visible={showPassword} onToggle={() => setShowPassword((value) => !value)} testId="register-password-input" autoComplete="new-password" />
        <PasswordInput label="Confirm Password" value={confirmPassword} onChange={setConfirmPassword} visible={showPassword} onToggle={() => setShowPassword((value) => !value)} testId="register-password-confirm-input" autoComplete="new-password" />
        <button type="submit" disabled={submitting} className="btn-blue w-full" data-testid="register-submit-button">
          {submitting ? "Creating account..." : "Create Admin Account"} <ArrowRight className="h-4 w-4" />
        </button>
        <p className="text-center text-sm text-slate-500">Already registered? <Link to="/admin/login" className="font-semibold text-blue-600 hover:underline" data-testid="register-login-link">Sign in</Link></p>
      </form>
    </AdminAuthFrame>
  );
};

type InputProps = {
  icon: React.ElementType;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId: string;
  autoComplete: string;
};

const AuthInput: React.FC<InputProps> = ({ icon: Icon, label, type = "text", value, onChange, placeholder, testId, autoComplete }) => (
  <label className="block text-xs font-semibold text-slate-600">
    {label}
    <span className="relative mt-1.5 block">
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input required type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} autoComplete={autoComplete} className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid={testId} />
    </span>
  </label>
);

type PasswordProps = Omit<InputProps, "icon" | "type" | "placeholder"> & { visible: boolean; onToggle: () => void };

const PasswordInput: React.FC<PasswordProps> = ({ label, value, onChange, testId, autoComplete, visible, onToggle }) => (
  <label className="block text-xs font-semibold text-slate-600">
    {label}
    <span className="relative mt-1.5 block">
      <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input required minLength={8} type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} placeholder="At least 8 characters" autoComplete={autoComplete} className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-10 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid={testId} />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={visible ? "Hide password" : "Show password"}>{visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </span>
  </label>
);

export default AdminRegister;
